import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  Paper,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  customerSupportAdminService,
  modifyUserInventoryService,
} from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import ShopAutocomplete from '../components/customer-support/ShopAutocomplete'
import ArticleAutocomplete from '../components/customer-support/ArticleAutocomplete'
import ReportsVerificationPanel from '../components/customer-support/ReportsVerificationPanel'
import QueryBuilderPanel from '../components/customer-support/QueryBuilderPanel'
import CsCustomerImportPanel from '../components/customer-support/CsCustomerImportPanel'
import ExportDialog from '../components/customer-support/ExportDialog'
import ArtifactViewerDialog from '../components/customer-support/ArtifactViewerDialog'
import {
  actionTypeLabel,
  artifactKindMeta,
  resolveArtifactPayload,
} from '../components/customer-support/artifactLabels'

const TAB = {
  AUDIT: 0,
  REPORTS: 1,
  PRICE_RCA: 2,
  QUERY: 3,
  CUSTOMERS: 4,
  ADJUST: 5,
  WIPE: 6,
}

const newRcaRow = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  shop: null,
  article: null,
  loading: false,
  result: null,
  error: '',
})

const lotId = (lot) => lot?.inventoryId ?? lot?.id
const lotQty = (lot) => lot?.quantityOnHand ?? lot?.quantity_on_hand
const lotSp = (lot) => lot?.unitCost ?? lot?.unit_cost
const lotMrp = (lot) => lot?.unitPrice ?? lot?.unit_price
const lotName = (lot) =>
  lot?.productName || lot?.product_name || lot?.admin_product_name || '—'
const lotSku = (lot) => lot?.articleCode || lot?.sku || lot?.article_code || '—'
const lotReserved = (lot) =>
  lot?.quantityOrdered ?? lot?.quantity_ordered ?? lot?.reserved_quantity ?? 0
const lotSourceLabel = (lot) =>
  (lot?.isNonShwapno ?? lot?.is_non_shwapno) ? 'Non-Shwapno' : 'Shwapno'

export default function CustomerSupportPage() {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.CUSTOMER_SUPPORT,
    'write',
  )

  const [tab, setTab] = useState(TAB.AUDIT)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)

  // Audit
  const [actions, setActions] = useState([])
  const [auditPagination, setAuditPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  })
  const [auditShop, setAuditShop] = useState(null)
  const [auditSearch, setAuditSearch] = useState('')
  const [auditActionType, setAuditActionType] = useState('')
  const [auditStatus, setAuditStatus] = useState('')
  const [auditCreatedFrom, setAuditCreatedFrom] = useState('')
  const [auditCreatedTo, setAuditCreatedTo] = useState('')
  const [auditSortOrder, setAuditSortOrder] = useState('DESC')

  // Price RCA
  const [rcaRows, setRcaRows] = useState([newRcaRow()])

  // Adjust
  const [adjShop, setAdjShop] = useState(null)
  const [adjNameFilter, setAdjNameFilter] = useState('')
  const [adjCodeFilter, setAdjCodeFilter] = useState('')
  const [adjLots, setAdjLots] = useState([])
  const [adjLotsTotal, setAdjLotsTotal] = useState(0)
  const [adjLotsPage, setAdjLotsPage] = useState(0)
  const [adjLotsRowsPerPage, setAdjLotsRowsPerPage] = useState(20)
  const [adjLotsLoading, setAdjLotsLoading] = useState(false)
  const [selectedLotIds, setSelectedLotIds] = useState([])
  const [lotEdits, setLotEdits] = useState({})
  const [adjReason, setAdjReason] = useState('')
  const [adjPreview, setAdjPreview] = useState(null)
  const [adjActionId, setAdjActionId] = useState('')

  // Wipe
  const [wipeShop, setWipeShop] = useState(null)
  const [wipeReason, setWipeReason] = useState('')
  const [wipeConfirm, setWipeConfirm] = useState('')
  const [wipePreview, setWipePreview] = useState(null)
  const [wipeActionId, setWipeActionId] = useState('')
  const [wipeStatus, setWipeStatus] = useState('')

  // Artifact viewer (same-page dialog)
  const [artifactOpen, setArtifactOpen] = useState(false)
  const [artifactKind, setArtifactKind] = useState('')
  const [artifactPayload, setArtifactPayload] = useState(null)
  const [artifactMeta, setArtifactMeta] = useState('')
  const [artifactLoading, setArtifactLoading] = useState(false)
  const [artifactError, setArtifactError] = useState('')

  const [exportOpen, setExportOpen] = useState(false)
  const [exportKind, setExportKind] = useState('audit')
  const [exportParams, setExportParams] = useState({})

  const wipeConfirmPhrase = wipeShop?.id ? `delete ${wipeShop.id}` : ''
  const wipeConfirmMatches =
    !!wipeConfirmPhrase && wipeConfirm.trim() === wipeConfirmPhrase

  const loadActions = useCallback(
    async (page = auditPagination.page) => {
      setLoading(true)
      setError('')
      try {
        const data = await customerSupportAdminService.listActions({
          orgId: auditShop?.id ? Number(auditShop.id) : undefined,
          search: auditSearch.trim() || undefined,
          actionType: auditActionType || undefined,
          status: auditStatus || undefined,
          createdFrom: auditCreatedFrom || undefined,
          createdTo: auditCreatedTo || undefined,
          sortBy: 'created_at',
          sortOrder: auditSortOrder,
          page,
          limit: auditPagination.limit,
        })
        setActions(data.items || [])
        setAuditPagination((prev) => ({
          ...prev,
          page: data.pagination?.page || page,
          total: data.pagination?.total || 0,
          limit: data.pagination?.limit || prev.limit,
        }))
      } catch (err) {
        setError(
          err?.response?.data?.message || err.message || 'Failed to load actions',
        )
      } finally {
        setLoading(false)
      }
    },
    [
      auditShop,
      auditSearch,
      auditActionType,
      auditStatus,
      auditCreatedFrom,
      auditCreatedTo,
      auditSortOrder,
      auditPagination.page,
      auditPagination.limit,
    ],
  )

  useEffect(() => {
    if (tab === TAB.AUDIT) {
      void loadActions(1)
    }
    // intentionally only when tab changes to audit — filters use Apply
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const loadAdjLots = useCallback(
    async (page = adjLotsPage) => {
      if (!adjShop?.id) {
        setAdjLots([])
        setAdjLotsTotal(0)
        return
      }
      setAdjLotsLoading(true)
      setError('')
      try {
        const search = [adjNameFilter.trim(), adjCodeFilter.trim()]
          .filter(Boolean)
          .join(' ')
        const data = await modifyUserInventoryService.getInventoryList(
          adjShop.id,
          {
            page: page + 1,
            limit: adjLotsRowsPerPage,
            search: search || undefined,
          },
        )
        const items = data.items || data.data || []
        setAdjLots(Array.isArray(items) ? items : [])
        setAdjLotsTotal(data.pagination?.total ?? items.length)
        setAdjLotsPage(page)
      } catch (err) {
        setAdjLots([])
        setAdjLotsTotal(0)
        setError(
          err?.response?.data?.message ||
            err.message ||
            'Failed to load shop inventory lots',
        )
      } finally {
        setAdjLotsLoading(false)
      }
    },
    [adjShop, adjNameFilter, adjCodeFilter, adjLotsPage, adjLotsRowsPerPage],
  )

  useEffect(() => {
    if (tab !== TAB.ADJUST || !adjShop?.id) return undefined
    const timer = setTimeout(() => {
      void loadAdjLots(0)
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, adjShop?.id, adjNameFilter, adjCodeFilter, adjLotsRowsPerPage])

  const toggleLot = (lot) => {
    const id = String(lotId(lot))
    setSelectedLotIds((prev) => {
      if (prev.includes(id)) {
        setLotEdits((edits) => {
          const next = { ...edits }
          delete next[id]
          return next
        })
        return prev.filter((x) => x !== id)
      }
      setLotEdits((edits) => ({
        ...edits,
        [id]: {
          quantityOnHand: lotQty(lot) != null ? String(lotQty(lot)) : '',
          unitCost: lotSp(lot) != null ? String(lotSp(lot)) : '',
          unitPrice: lotMrp(lot) != null ? String(lotMrp(lot)) : '',
        },
      }))
      return [...prev, id]
    })
    setAdjPreview(null)
    setAdjActionId('')
  }

  const updateLotEdit = (id, field, value) => {
    setLotEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }))
  }

  const runAdjustBulkDryRun = async () => {
    if (!adjShop?.id || !selectedLotIds.length) {
      setError('Select a shop and at least one inventory lot')
      return
    }
    if (adjReason.trim().length < 8) {
      setError('Reason must be at least 8 characters (shown to the grocer)')
      return
    }
    setLoading(true)
    setError('')
    setInfo('')
    try {
      const items = selectedLotIds.map((id) => {
        const edit = lotEdits[id] || {}
        const item = { inventoryId: Number(id) }
        if (edit.quantityOnHand !== '' && edit.quantityOnHand != null) {
          item.quantityOnHand = Number(edit.quantityOnHand)
        }
        if (edit.unitCost !== '' && edit.unitCost != null) {
          item.unitCost = Number(edit.unitCost)
        }
        if (edit.unitPrice !== '' && edit.unitPrice != null) {
          item.unitPrice = Number(edit.unitPrice)
        }
        return item
      })
      const data = await customerSupportAdminService.adjustBulkDryRun({
        orgId: Number(adjShop.id),
        items,
        reason: adjReason.trim(),
      })
      setAdjPreview(data.preview)
      setAdjActionId(data.action?.id || '')
      setInfo(
        `Bulk dry-run ready for ${items.length} lot(s)${
          data.preview?.transportHint === 'kafka'
            ? ' (large batch — kafka transport flagged)'
            : ''
        }`,
      )
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || 'Bulk adjust dry-run failed',
      )
    } finally {
      setLoading(false)
    }
  }

  const runAdjustApply = async () => {
    if (!adjActionId) return
    setLoading(true)
    setError('')
    try {
      const data = await customerSupportAdminService.adjustApply(adjActionId)
      const result = data.result || {}
      setInfo(
        `Adjust applied — succeeded ${result.succeeded ?? 1}, failed ${
          result.failed ?? 0
        }`,
      )
      setAdjPreview(null)
      setAdjActionId('')
      setSelectedLotIds([])
      setLotEdits({})
      void loadAdjLots(adjLotsPage)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Adjust apply failed')
    } finally {
      setLoading(false)
    }
  }

  const updateRcaRow = (key, patch) => {
    setRcaRows((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    )
  }

  const diagnoseCombination = async (key, shop, article) => {
    if (!shop?.id || !article?.sku) {
      updateRcaRow(key, { error: 'Select shop and article first' })
      return
    }
    updateRcaRow(key, { loading: true, error: '', result: null })
    try {
      const data = await customerSupportAdminService.priceRca({
        orgId: Number(shop.id),
        sku: String(article.sku).trim(),
      })
      updateRcaRow(key, { loading: false, result: data, error: '' })
      setInfo(`Price RCA ready for ${shop.label} × ${article.label}`)
    } catch (err) {
      updateRcaRow(key, {
        loading: false,
        error: err?.response?.data?.message || err.message || 'Price RCA failed',
      })
    }
  }

  const runWipeDryRun = async () => {
    if (!wipeShop?.id) {
      setError('Select a shop')
      return
    }
    if (!wipeConfirmMatches) {
      setError(`Type exactly "${wipeConfirmPhrase}" to confirm`)
      return
    }
    if (wipeReason.trim().length < 8) {
      setError('Reason must be at least 8 characters (shown to the grocer)')
      return
    }
    setLoading(true)
    setError('')
    setInfo('')
    try {
      const data = await customerSupportAdminService.wipeDryRun({
        orgId: Number(wipeShop.id),
        reason: wipeReason.trim(),
      })
      setWipePreview(data.preview)
      setWipeActionId(data.action?.id || '')
      setWipeStatus(data.action?.status || '')
      setInfo('Wipe dry-run ready — submit for second-party approval')
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Wipe dry-run failed')
    } finally {
      setLoading(false)
    }
  }

  const wipeStep = async (fn, label, { requireConfirm = false } = {}) => {
    if (!wipeActionId) return
    if (requireConfirm && !wipeConfirmMatches) {
      setError(`Type exactly "${wipeConfirmPhrase}" before executing`)
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await fn(wipeActionId)
      setWipeStatus(data.status || data.action?.status || label)
      setInfo(`${label} ok`)
      if (data.action) setWipeStatus(data.action.status)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || `${label} failed`)
    } finally {
      setLoading(false)
    }
  }

  const openArtifact = async (action, artifact) => {
    const kind = artifact?.kind || ''
    const meta = artifactKindMeta(kind)
    setArtifactKind(kind)
    setArtifactMeta(
      [
        action?.action_type,
        action?.shop_name
          ? `${action.shop_name} (${action.org_id})`
          : action?.org_id
            ? `Shop ${action.org_id}`
            : null,
        action?.created_at
          ? new Date(action.created_at).toLocaleString()
          : null,
      ]
        .filter(Boolean)
        .join(' · '),
    )
    setArtifactError('')
    setArtifactOpen(true)

    const inline = resolveArtifactPayload(action, kind)
    if (inline != null) {
      setArtifactPayload(inline)
      setArtifactLoading(false)
      return
    }

    // Fallback: authenticated API proxies S3 (browser must not fetch S3 directly).
    setArtifactLoading(true)
    setArtifactPayload(null)
    try {
      const data = await customerSupportAdminService.downloadArtifact(artifact.id)
      if (data == null) {
        throw new Error(`Could not load ${meta.label}`)
      }
      if (typeof data === 'string') {
        try {
          setArtifactPayload(JSON.parse(data))
        } catch {
          setArtifactPayload({ content: data })
        }
      } else {
        setArtifactPayload(data)
      }
    } catch (err) {
      setArtifactError(
        err?.response?.data?.message ||
          err?.message ||
          `Failed to load ${meta.label.toLowerCase()}`,
      )
    } finally {
      setArtifactLoading(false)
    }
  }

  const visibleTabs = useMemo(() => {
    const base = [
      { value: TAB.AUDIT, label: 'Audit' },
      { value: TAB.REPORTS, label: 'Reports verification' },
      { value: TAB.PRICE_RCA, label: 'Price RCA' },
      { value: TAB.QUERY, label: 'Look up data' },
      { value: TAB.CUSTOMERS, label: 'Customers' },
    ]
    if (canWrite) {
      base.push({ value: TAB.ADJUST, label: 'Lot Update' })
      base.push({ value: TAB.WIPE, label: 'Inventory wipe' })
    }
    return base
  }, [canWrite])

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Customer Support
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sensitive grocer tools. Pick shops and articles from search. Write
        actions need Senior CS / Superuser. Wipe needs a second approver and a
        typed confirmation.
      </Typography>

      {!canWrite ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only access. Mutating tools (Customers upload, Lot Update,
          Inventory wipe) are hidden or limited.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {info ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        {visibleTabs.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {tab === TAB.AUDIT && (
        <Card>
          <CardContent>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <ShopAutocomplete
                  value={auditShop}
                  onChange={setAuditShop}
                  helperText="Optional shop filter"
                />
                <TextField
                  size="small"
                  label="Search"
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  fullWidth
                  helperText="Reason, email, type, status, shop id, action id"
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Action type"
                  value={auditActionType}
                  onChange={(e) => setAuditActionType(e.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[
                    'INVENTORY_WIPE',
                    'QTY_ADJUST',
                    'PRICE_ADJUST',
                    'BULK_ADJUST',
                    'REPORTS_VIEW',
                    'PRICE_RCA',
                    'DATA_LOOKUP',
                    'EXPORT',
                    'CUSTOMER_BULK_IMPORT',
                  ].map((t) => (
                    <MenuItem key={t} value={t}>
                      {actionTypeLabel(t)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={auditStatus}
                  onChange={(e) => setAuditStatus(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[
                    'DRAFT_DRY_RUN',
                    'PENDING_APPROVAL',
                    'APPROVED',
                    'RUNNING',
                    'SUCCEEDED',
                    'FAILED',
                    'REJECTED',
                  ].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={auditCreatedFrom}
                  onChange={(e) => setAuditCreatedFrom(e.target.value)}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={auditCreatedTo}
                  onChange={(e) => setAuditCreatedTo(e.target.value)}
                />
                <TextField
                  select
                  size="small"
                  label="Date sort"
                  value={auditSortOrder}
                  onChange={(e) => setAuditSortOrder(e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="DESC">Newest first</MenuItem>
                  <MenuItem value="ASC">Oldest first</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  onClick={() => void loadActions(1)}
                  sx={{ height: 40 }}
                >
                  Apply filters
                </Button>
                <Button
                  variant="outlined"
                  sx={{ height: 40 }}
                  onClick={() => {
                    setExportKind('audit')
                    setExportParams({
                      orgId: auditShop?.id ? Number(auditShop.id) : undefined,
                      search: auditSearch.trim() || undefined,
                      actionType: auditActionType || undefined,
                      status: auditStatus || undefined,
                      createdFrom: auditCreatedFrom || undefined,
                      createdTo: auditCreatedTo || undefined,
                      sortOrder: auditSortOrder,
                    })
                    setExportOpen(true)
                  }}
                >
                  Export CSV
                </Button>
              </Stack>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Shop</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Artifacts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actions.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>{actionTypeLabel(row.action_type)}</TableCell>
                    <TableCell>
                      {row.shop_name
                        ? `${row.shop_name} (${row.org_id})`
                        : row.org_id}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                      {row.reason || '—'}
                    </TableCell>
                    <TableCell>
                      {row.requested_by_email || row.requested_by}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(row.artifacts || []).map((a) => {
                          const meta = artifactKindMeta(a.kind)
                          return (
                            <Chip
                              key={a.id}
                              size="small"
                              clickable
                              color={meta.color}
                              variant="outlined"
                              label={meta.label}
                              onClick={() => void openArtifact(row, a)}
                              sx={{ fontWeight: 600 }}
                            />
                          )
                        })}
                        {!row.artifacts?.length ? '—' : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!actions.length ? (
                  <TableRow>
                    <TableCell colSpan={7}>No actions match filters</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={auditPagination.total}
              page={Math.max(0, (auditPagination.page || 1) - 1)}
              onPageChange={(_, p) => void loadActions(p + 1)}
              rowsPerPage={auditPagination.limit}
              rowsPerPageOptions={[auditPagination.limit]}
            />
          </CardContent>
        </Card>
      )}

      {tab === TAB.REPORTS && (
        <Card>
          <CardContent>
            <ReportsVerificationPanel
              onError={setError}
              onInfo={setInfo}
              onExportRequest={(params) => {
                setExportKind('reports')
                setExportParams(params)
                setExportOpen(true)
              }}
            />
          </CardContent>
        </Card>
      )}

      {tab === TAB.QUERY && (
        <Card>
          <CardContent>
            <QueryBuilderPanel onError={setError} onInfo={setInfo} />
          </CardContent>
        </Card>
      )}

      {tab === TAB.PRICE_RCA && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setRcaRows((rows) => [...rows, newRcaRow()])}
            >
              Add combination
            </Button>
          </Stack>
          {rcaRows.map((row, index) => (
            <Card key={row.key} variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography fontWeight={600}>
                    Combination {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={rcaRows.length === 1}
                    onClick={() =>
                      setRcaRows((rows) => rows.filter((r) => r.key !== row.key))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack spacing={2}>
                  <ShopAutocomplete
                    value={row.shop}
                    onChange={(shop) =>
                      updateRcaRow(row.key, { shop, result: null, error: '' })
                    }
                    required
                  />
                  <ArticleAutocomplete
                    value={row.article}
                    onChange={(article) =>
                      updateRcaRow(row.key, {
                        article,
                        result: null,
                        error: '',
                      })
                    }
                    required
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={!row.shop || !row.article || row.loading}
                      onClick={() =>
                        void diagnoseCombination(row.key, row.shop, row.article)
                      }
                    >
                      {row.loading ? 'Diagnosing…' : 'Diagnose'}
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={!row.shop || !row.article}
                      onClick={() => {
                        setExportKind('price_rca')
                        setExportParams({
                          orgId: Number(row.shop.id),
                          sku: String(row.article.sku || '').trim(),
                        })
                        setExportOpen(true)
                      }}
                    >
                      Export CSV
                    </Button>
                  </Stack>
                </Stack>
                {row.error ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {row.error}
                  </Alert>
                ) : null}
                {row.result ? (
                  <Box sx={{ mt: 2 }}>
                    {(row.result.findings || []).length ? (
                      (row.result.findings || []).map((f) => (
                        <Alert key={f} severity="warning" sx={{ mb: 1 }}>
                          {f}
                        </Alert>
                      ))
                    ) : (
                      <Alert severity="success">No automatic findings</Alert>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2">
                      Store lots ({(row.result.userLots || []).length})
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Lot ID</TableCell>
                          <TableCell>SP</TableCell>
                          <TableCell>MRP</TableCell>
                          <TableCell>QoH</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(row.result.userLots || []).map((lot) => (
                          <TableRow key={lot.id}>
                            <TableCell>{lot.id}</TableCell>
                            <TableCell>{lot.unit_cost}</TableCell>
                            <TableCell>{lot.unit_price}</TableCell>
                            <TableCell>{lot.quantity_on_hand}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {tab === TAB.CUSTOMERS && (
        <CsCustomerImportPanel
          canWrite={canWrite}
          onError={setError}
          onInfo={setInfo}
          loading={loading}
          setLoading={setLoading}
        />
      )}

      {canWrite && tab === TAB.ADJUST && (
        <Stack spacing={2}>
          <Alert severity="info">
            Update store lots for a shop. Quantity on hand and selling price /
            MRP can be changed from here (single lot or bulk).
          </Alert>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                1. Shop & lot filters
              </Typography>
              <Stack spacing={2}>
                <ShopAutocomplete
                  value={adjShop}
                  onChange={(shop) => {
                    setAdjShop(shop)
                    setSelectedLotIds([])
                    setLotEdits({})
                    setAdjPreview(null)
                    setAdjActionId('')
                  }}
                  required
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    size="small"
                    label="Article name (partial)"
                    value={adjNameFilter}
                    onChange={(e) => setAdjNameFilter(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Article code / SKU (partial)"
                    value={adjCodeFilter}
                    onChange={(e) => setAdjCodeFilter(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    disabled={!adjShop}
                    onClick={() => void loadAdjLots(0)}
                    sx={{ height: 40, whiteSpace: 'nowrap' }}
                  >
                    Search lots
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  2. Select inventory lots (multi-select)
                </Typography>
                <Chip
                  size="small"
                  color="primary"
                  label={`${selectedLotIds.length} selected`}
                />
              </Stack>
              {!adjShop ? (
                <Alert severity="info">Select a shop to browse lots.</Alert>
              ) : adjLotsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Lot ID</TableCell>
                        <TableCell>Article</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>QoH</TableCell>
                        <TableCell>SP</TableCell>
                        <TableCell>MRP</TableCell>
                        <TableCell>Reserved</TableCell>
                        <TableCell>Lot Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adjLots.map((lot) => {
                        const id = String(lotId(lot))
                        const checked = selectedLotIds.includes(id)
                        return (
                          <TableRow
                            key={id}
                            hover
                            selected={checked}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleLot(lot)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={checked} />
                            </TableCell>
                            <TableCell>{id}</TableCell>
                            <TableCell>{lotName(lot)}</TableCell>
                            <TableCell>{lotSku(lot)}</TableCell>
                            <TableCell>{lotQty(lot)}</TableCell>
                            <TableCell>{lotSp(lot)}</TableCell>
                            <TableCell>{lotMrp(lot)}</TableCell>
                            <TableCell>{lotReserved(lot)}</TableCell>
                            <TableCell>{lotSourceLabel(lot)}</TableCell>
                          </TableRow>
                        )
                      })}
                      {!adjLots.length ? (
                        <TableRow>
                          <TableCell colSpan={9}>No lots found</TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={adjLotsTotal}
                    page={adjLotsPage}
                    onPageChange={(_, p) => void loadAdjLots(p)}
                    rowsPerPage={adjLotsRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setAdjLotsRowsPerPage(parseInt(e.target.value, 10))
                      setAdjLotsPage(0)
                    }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                  />
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                3. Edit selected lots & apply in bulk
              </Typography>
              {!selectedLotIds.length ? (
                <Alert severity="info">
                  Select one or more lots above. You can change qty / SP / MRP
                  per lot, then dry-run once for all.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {selectedLotIds.map((id) => {
                    const lot = adjLots.find((l) => String(lotId(l)) === id)
                    const edit = lotEdits[id] || {}
                    return (
                      <Paper key={id} variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Lot {id}
                          {lot ? ` · ${lotName(lot)} (${lotSku(lot)})` : ''}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            size="small"
                            label="Quantity on hand"
                            value={edit.quantityOnHand ?? ''}
                            onChange={(e) =>
                              updateLotEdit(id, 'quantityOnHand', e.target.value)
                            }
                          />
                          <TextField
                            size="small"
                            label="Selling price (SP)"
                            value={edit.unitCost ?? ''}
                            onChange={(e) =>
                              updateLotEdit(id, 'unitCost', e.target.value)
                            }
                          />
                          <TextField
                            size="small"
                            label="MRP"
                            value={edit.unitPrice ?? ''}
                            onChange={(e) =>
                              updateLotEdit(id, 'unitPrice', e.target.value)
                            }
                          />
                        </Stack>
                      </Paper>
                    )
                  })}
                  <TextField
                    size="small"
                    label="Reason (required — shown to grocer)"
                    value={adjReason}
                    onChange={(e) => setAdjReason(e.target.value)}
                    multiline
                    minRows={3}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      disabled={adjReason.trim().length < 8}
                      onClick={() => void runAdjustBulkDryRun()}
                    >
                      Bulk dry run
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!adjActionId}
                      onClick={() => void runAdjustApply()}
                    >
                      Apply bulk
                    </Button>
                  </Stack>
                </Stack>
              )}
              {adjPreview ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">
                    Preview ({adjPreview.itemCount} lots
                    {adjPreview.blockedCount
                      ? `, ${adjPreview.blockedCount} blocked`
                      : ''}
                    )
                  </Typography>
                  <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 320 }}>
                    {JSON.stringify(adjPreview, null, 2)}
                  </pre>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Stack>
      )}

      {canWrite && tab === TAB.WIPE && (
        <Card>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This permanently clears all store inventory for the selected shop
              (lots + line items). It requires a second write-capable approver.
            </Alert>
            <Stack spacing={2} sx={{ mb: 2, maxWidth: 640 }}>
              <ShopAutocomplete
                value={wipeShop}
                onChange={(shop) => {
                  setWipeShop(shop)
                  setWipeConfirm('')
                  setWipePreview(null)
                  setWipeActionId('')
                  setWipeStatus('')
                }}
                required
              />
              <TextField
                size="small"
                label="Reason (required — shown to grocer)"
                value={wipeReason}
                onChange={(e) => setWipeReason(e.target.value)}
                multiline
                minRows={3}
              />

              {wipeShop ? (
                <Alert severity="warning" variant="outlined">
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Confirmation required
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    To continue, type the following phrase <strong>exactly</strong>{' '}
                    in the box below (including the shop id):
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: '#FEF3C7',
                      border: '2px solid #F59E0B',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontWeight: 700,
                      fontSize: 16,
                      letterSpacing: 0.3,
                    }}
                  >
                    {wipeConfirmPhrase}
                  </Box>
                </Alert>
              ) : (
                <Alert severity="info">
                  Select a shop first. You will then be asked to type{' '}
                  <strong>delete {'{shop_id}'}</strong> to confirm.
                </Alert>
              )}

              <TextField
                size="small"
                label="Confirmation phrase"
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                disabled={!wipeShop}
                placeholder={wipeConfirmPhrase || 'delete {shop_id}'}
                error={!!wipeConfirm && !wipeConfirmMatches}
                helperText={
                  wipeConfirm && !wipeConfirmMatches
                    ? 'Phrase does not match yet'
                    : wipeConfirmMatches
                      ? 'Confirmation matched — you may continue'
                      : ' '
                }
              />
              <Button
                variant="outlined"
                color="error"
                disabled={
                  !wipeShop ||
                  !wipeConfirmMatches ||
                  wipeReason.trim().length < 8
                }
                onClick={() => void runWipeDryRun()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Dry run
              </Button>
            </Stack>
            {wipeActionId ? (
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Action ${wipeActionId}`} />
                <Chip label={wipeStatus || '—'} color="primary" />
                <Button
                  size="small"
                  onClick={() =>
                    void wipeStep(
                      customerSupportAdminService.wipeSubmit,
                      'Submit',
                    )
                  }
                >
                  Submit
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    void wipeStep(
                      customerSupportAdminService.wipeApprove,
                      'Approve',
                    )
                  }
                >
                  Approve (other user)
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    void wipeStep(
                      (id) => customerSupportAdminService.wipeReject(id, {}),
                      'Reject',
                    )
                  }
                >
                  Reject
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={!wipeConfirmMatches}
                  onClick={() =>
                    void wipeStep(
                      customerSupportAdminService.wipeExecute,
                      'Execute',
                      { requireConfirm: true },
                    )
                  }
                >
                  Execute
                </Button>
              </Stack>
            ) : null}
            {wipePreview ? (
              <pre style={{ fontSize: 12, overflow: 'auto' }}>
                {JSON.stringify(wipePreview, null, 2)}
              </pre>
            ) : null}
          </CardContent>
        </Card>
      )}

      <ArtifactViewerDialog
        open={artifactOpen}
        onClose={() => setArtifactOpen(false)}
        kind={artifactKind}
        payload={artifactPayload}
        actionMeta={artifactMeta}
        loading={artifactLoading}
        error={artifactError}
      />

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        kind={exportKind}
        requestParams={exportParams}
        onError={setError}
        onInfo={setInfo}
      />
    </Container>
  )
}
